import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw, AlertCircle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { getCalendarStatus, getCalendarEvents } from '../services/apiService';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'moment/locale/es';

moment.locale('es');
const localizer = momentLocalizer(moment);

const Calendar = () => {
  const [loading, setLoading] = useState(true);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarEmail, setCalendarEmail] = useState('');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month', 'week', 'day', 'agenda'

  const loadCalendarStatus = async () => {
    try {
      const response = await getCalendarStatus();
      if (response.success) {
        setCalendarConnected(response.connected);
        setCalendarEmail(response.email || '');
      }
    } catch (error) {
      console.error('Error cargando estado de Calendar:', error);
    }
  };

  const loadEvents = useCallback(async (startDate = null, endDate = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // Calcular rango de fechas según la vista actual
      let timeMin, timeMax;
      
      if (startDate && endDate) {
        timeMin = startDate.toISOString();
        timeMax = endDate.toISOString();
      } else {
        // Por defecto, cargar eventos del mes actual
        const now = currentDate || new Date();
        const start = moment(now).startOf('month').subtract(7, 'days').toDate();
        const end = moment(now).endOf('month').add(7, 'days').toDate();
        timeMin = start.toISOString();
        timeMax = end.toISOString();
      }
      
      const response = await getCalendarEvents(timeMin, timeMax, 250);
      
      if (response.success) {
        const rawEvents = response.events || [];
        
        // Convertir eventos de Google Calendar al formato de react-big-calendar
        const formattedEvents = rawEvents.map(event => {
          const start = event.start?.dateTime 
            ? new Date(event.start.dateTime)
            : event.start?.date 
              ? moment(event.start.date).startOf('day').toDate()
              : new Date();
          
          const end = event.end?.dateTime 
            ? new Date(event.end.dateTime)
            : event.end?.date 
              ? moment(event.end.date).endOf('day').toDate()
              : new Date();
          
          return {
            id: event.id,
            title: event.summary || 'Sin título',
            start: start,
            end: end,
            allDay: !event.start?.dateTime && !!event.start?.date,
            resource: {
              description: event.description,
              location: event.location,
              attendees: event.attendees,
              htmlLink: event.htmlLink,
              color: event.colorId ? `color-${event.colorId}` : null
            }
          };
        });
        
        setCalendarEvents(formattedEvents);
      } else {
        throw new Error(response.error || 'Error al cargar eventos');
      }
    } catch (error) {
      console.error('Error cargando eventos:', error);
      setError(error.message || 'Error al cargar eventos del calendario');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadCalendarStatus();
  }, []);

  useEffect(() => {
    if (calendarConnected) {
      loadEvents();
    }
  }, [calendarConnected, currentDate, view, loadEvents]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadEvents();
    setRefreshing(false);
  };

  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const handleViewChange = useCallback((newView) => {
    setView(newView);
  }, []);

  const handleRangeChange = useCallback((range) => {
    if (range && range.length > 0) {
      const start = range[0];
      const end = range[range.length - 1];
      loadEvents(start, end);
    }
  }, [loadEvents]);

  const handleSelectEvent = (event) => {
    if (event.resource?.htmlLink) {
      window.open(event.resource.htmlLink, '_blank');
    }
  };

  const navigateToToday = () => {
    setCurrentDate(new Date());
  };

  const navigatePrevious = () => {
    if (view === 'month') {
      setCurrentDate(moment(currentDate).subtract(1, 'month').toDate());
    } else if (view === 'week') {
      setCurrentDate(moment(currentDate).subtract(1, 'week').toDate());
    } else if (view === 'day') {
      setCurrentDate(moment(currentDate).subtract(1, 'day').toDate());
    }
  };

  const navigateNext = () => {
    if (view === 'month') {
      setCurrentDate(moment(currentDate).add(1, 'month').toDate());
    } else if (view === 'week') {
      setCurrentDate(moment(currentDate).add(1, 'week').toDate());
    } else if (view === 'day') {
      setCurrentDate(moment(currentDate).add(1, 'day').toDate());
    }
  };

  if (!calendarConnected) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Calendario</h1>
          <p className="text-muted-foreground mt-2">
            Visualiza y gestiona tus eventos de Google Calendar
          </p>
        </div>
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Calendar no conectado</h3>
            <p className="text-muted-foreground mb-4">
              Para ver tus eventos, necesitas conectar tu cuenta de Google Calendar en Configuración → Integraciones.
            </p>
            <Button onClick={() => window.location.href = '/#/configuracion/integraciones'}>
              Ir a Configuración
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Calendario</h1>
          <p className="text-muted-foreground mt-2">
            {calendarConnected ? `Conectado como ${calendarEmail}` : 'Visualiza y gestiona tus eventos'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing || loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {!calendarConnected ? (
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Calendar no conectado</h3>
            <p className="text-muted-foreground mb-4">
              Para ver tus eventos, necesitas conectar tu cuenta de Google Calendar en Configuración → Integraciones.
            </p>
            <Button onClick={() => window.location.href = '/#/configuracion/integraciones'}>
              Ir a Configuración
            </Button>
          </CardContent>
        </Card>
      ) : loading ? (
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Cargando eventos...</p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h3 className="text-lg font-semibold mb-2">Error</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={handleRefresh} variant="outline">
              Reintentar
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-0">
            {/* Controles de navegación */}
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigatePrevious}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateToToday}
                >
                  Hoy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateNext}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <div className="ml-4 text-lg font-semibold">
                  {view === 'month' && moment(currentDate).format('MMMM YYYY')}
                  {view === 'week' && `${moment(currentDate).startOf('week').format('D MMM')} - ${moment(currentDate).endOf('week').format('D MMM YYYY')}`}
                  {view === 'day' && moment(currentDate).format('dddd, D [de] MMMM [de] YYYY')}
                  {view === 'agenda' && 'Agenda'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={view === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleViewChange('month')}
                >
                  Mes
                </Button>
                <Button
                  variant={view === 'week' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleViewChange('week')}
                >
                  Semana
                </Button>
                <Button
                  variant={view === 'day' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleViewChange('day')}
                >
                  Día
                </Button>
                <Button
                  variant={view === 'agenda' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleViewChange('agenda')}
                >
                  Agenda
                </Button>
              </div>
            </div>

            {/* Calendario */}
            <div className="p-4" style={{ height: '700px' }}>
              <BigCalendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                view={view}
                onView={handleViewChange}
                date={currentDate}
                onNavigate={handleNavigate}
                onRangeChange={handleRangeChange}
                onSelectEvent={handleSelectEvent}
                components={{
                  toolbar: () => null
                }}
                scrollToTime={new Date(2024, 0, 1, 9, 0, 0)}
                min={new Date(2024, 0, 1, 0, 0, 0)}
                max={new Date(2024, 0, 1, 23, 59, 59)}
                messages={{
                  next: 'Siguiente',
                  previous: 'Anterior',
                  today: 'Hoy',
                  month: 'Mes',
                  week: 'Semana',
                  day: 'Día',
                  agenda: 'Agenda',
                  date: 'Fecha',
                  time: 'Hora',
                  event: 'Evento',
                  noEventsInRange: 'No hay eventos en este rango de fechas.'
                }}
                eventPropGetter={(event) => {
                  return {
                    style: {
                      backgroundColor: event.resource?.color || '#3174ad',
                      borderColor: event.resource?.color || '#3174ad',
                      color: '#fff'
                    }
                  };
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Calendar;
